import React, { useState, useEffect, useRef } from "react";
import {
  Printer,
  X,
  Search,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  RefreshCcw,
} from "lucide-react";
import { qPrinterService } from "../integrations/QPrinter/QPrinterService.js";
import { useBluetooth } from "../hooks/useBluetooth";
import { isReactNativeWebView } from "../utils/bluetoothBridge.js";
import PrinterGrid from "./PrinterGrid";
import { generateUUID } from "../utils/common.js";
import { defaultPrinterPrintConfig } from "../utils/thermalPrinterConfig.js";
import "../assets/styles/animations.css";

const PRINTER_TYPES = [
  {
    id: "bluetooth",
    name: "Bluetooth Printer",
    icon: Printer,
    description: "Connect to nearby Bluetooth printers",
    enabled: true,
  },
  {
    id: "network",
    name: "Network Printer",
    icon: Printer,
    description: "Connect to printers on your network",
    enabled: true,
  },
  {
    id: "usb",
    name: "USB Printer",
    icon: Printer,
    description: "Connect to directly attached USB printers",
    enabled: false,
  },
];

const sampleKOTData = {
  type: "New KOT",
  formattedDateTime: new Date().toLocaleString("en-IN"),
  billNo: "TEST_KOT",
  roomData: {
    table: "T4",
    room: "Dine In",
  },
  customer: {
    name: "John Doe",
    phone: "1234567890",
    address: "123 Test Street, Test City",
  },
  cartItems: [
    {
      name: "Butter Chicken",
      quantity: 2,
      isVariant: true,
      variantName: "Full",
      comment: "Extra butter, medium spicy",
      canceled: false,
    },
    {
      name: "Naan",
      quantity: 4,
      isVariant: false,
      comment: "Butter naan, well cooked",
      canceled: false,
    },
  ],
  cancelItems: [
    {
      name: "Coke",
      quantity: 1,
      isVariant: false,
    },
  ],
  comment: "Priority order - VIP customer",
};

const LOCATIONS = [
  { id: "kot", name: "KOT" },
  { id: "bill", name: "BILL" },
  // { id: "bar", name: "BAR" },
  { id: "all", name: "ALL" },
];

// Dummy printer data for simulation
const DUMMY_PRINTERS = [
  { id: 1, name: "Star TSP100", address: "00:11:22:33:44:55" },
  { id: 2, name: "Epson TM-T88VI", address: "66:77:88:99:AA:BB" },
  { id: 3, name: "Citizen CT-S310II", address: "CC:DD:EE:FF:00:11" },
];

export default function AddPrinter({ isOpen, onClose, onSave }) {
  const [step, setStep] = useState(2);
  const [selectedType, setSelectedType] = useState("qrapidServer");
  const [expandedPrinterId, setExpandedPrinterId] = useState(null);
  const [printerDetails, setPrinterDetails] = useState({});
  const [connectingPrinter, setConnectingPrinter] = useState(null);
  const [connectedPrinter, setConnectedPrinter] = useState(null);
  const [scannedQPrinters, setScannedQPrinters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const MAX_RETRIES = 3;
  const BASE_RETRY_DELAY = 1000;
  const retryTimeoutRef = useRef(null);
  const menuRef = useRef(null);

  const {
    status: bluetoothStatus,
    devices: scannedPrinters,
    isScanning,
    error: bluetoothError,
    requestPermission,
    enableBluetooth,
    disableBluetooth,
    openSettings,
    startScan,
    connectPrinter,
  } = useBluetooth({});

  const getRetryDelay = (retryCount) => {
    return Math.min(BASE_RETRY_DELAY * Math.pow(2, retryCount), 10000);
  };

  const fetchPrinters = async (currentRetry = 0) => {
    setIsLoading(true);
    setError(null);

    try {
      const printers = await qPrinterService.getAvailablePrinters();
      if (printers && printers.length > 0) {
        setScannedQPrinters(printers);
        setRetryCount(0);
        setIsRetrying(false);
        setTimeout(() => {
          setIsLoading(false);
        }, 2000);
      } else {
        throw new Error("No printers found");
      }
    } catch (err) {
      console.error("Failed to fetch printers:", err);

      if (currentRetry < MAX_RETRIES) {
        const delay = getRetryDelay(currentRetry);
        setIsRetrying(true);
        setError(
          `Attempt ${currentRetry + 1}/${MAX_RETRIES}: Retrying in ${
            delay / 1000
          } seconds...`
        );

        // Clear any existing timeout
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }

        // Set new timeout
        retryTimeoutRef.current = setTimeout(() => {
          setRetryCount(currentRetry + 1);
          fetchPrinters(currentRetry + 1);
        }, delay);
      } else {
        setError(
          `Failed to discover printers after ${MAX_RETRIES} attempts. Please check your connection and try again.`
        );
        setIsLoading(false);
        setIsRetrying(false);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchPrinters(0);
    }

    return () => {
      // Cleanup timeouts when component unmounts or when isOpen changes
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      setIsRetrying(false);
      setRetryCount(0);
      setError(null);
      setIsLoading(false);
    };
  }, [isOpen]); // Only depend on isOpen

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setExpandedPrinterId(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Check Bluetooth status when component mounts
  useEffect(() => {
    if (selectedType === "bluetooth") {
    }
  }, [selectedType]);

  const resetForm = () => {
    setStep(2);
    setSelectedType("qrapidServer");
    setExpandedPrinterId(null);
    setPrinterDetails({});
    setScannedQPrinters([]);
    setIsLoading(true);
    setConnectingPrinter(null);
  };

  const handleTypeSelect = async (type) => {
    if (type.enabled) {
      setSelectedType(type.id);
      setStep(2);

      if (type.id === "bluetooth") {
        // Request permission and check status when selecting Bluetooth
        await requestPermission();
        // getBluetoothStatus();
      }
      if (type.id === "network") {
        const printers = await qPrinterService.getAvailablePrinters();
        if (printers && printers.length > 0) {
          setScannedQPrinters(printers);
          console.log(printers);
        }
      }
    }
  };

  const simulateScan = () => {
    if (selectedType === "bluetooth") {
      if (bluetoothStatus === "PoweredOff") {
        enableBluetooth();
        return;
      }
      if (bluetoothStatus === "Unauthorized") {
        openSettings();
        return;
      }
      startScan();
    }
  };

  const handlePrinterClick = async (printer) => {
    if (expandedPrinterId === printer.innerMacAddress) {
      setExpandedPrinterId(null);
      return;
    }
    setConnectingPrinter(printer.innerMacAddress);
    try {
      await connectPrinter(printer);
    } catch (error) {
      console.error("Failed to connect:", error);
    }
  };

  const handleTestPrint = async (printer) => {
    try {
      const testContent = `
        <Text align='center' fontWidth='1' fontHeight='1'>Example text</Text>
        <NewLine />
      `;

      if (printer.type === "qprinter") {
        await qPrinterService.testWidthPrint(
          printerDetails[printer.innerMacAddress]
        );
        await qPrinterService.printKOT({
          printerName: printer.deviceName,
          kotData: sampleKOTData,
          type: "New KOT",
          printConfig: printerDetails[printer.innerMacAddress],
        });
      }
    } catch (error) {
      console.error("Test print failed:", error);
    }
  };

  const handlePrinterDetailChange = (printer, field, value) => {
    setPrinterDetails((prev) => {
      const current = defaultPrinterPrintConfig({
        ...printer,
        ...prev[printer.innerMacAddress],
      });

      return {
        ...prev,
        [printer.innerMacAddress]: {
          ...current,
          [field]: value,
        },
      };
    });
  };

  const handleSavePrinter = (printer) => {
    const details = printerDetails[printer.innerMacAddress] || {};

    if (!details.name || !details.location) return;

    const printConfig = defaultPrinterPrintConfig({
      ...printer,
      ...details,
    });

    onSave({
      ...printer,
      name: printConfig.name,
      location: printConfig.location,
      type: selectedType,
      id: generateUUID(),
      isConnected: true,
      width: printConfig.width,
      paperSize: printConfig.paperSize,
    });
    resetForm();
    onClose();
  };

  const handleRetry = () => {
    setRetryCount(0);
    setError(null);
    setIsLoading(true);
    setIsRetrying(false);
    fetchPrinters(0);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      style={{ zIndex: 500 }}
    >
      <div
        ref={menuRef}
        className="w-[calc(100vw-16px)] md:w-[calc(100vw-120px)] h-[calc(100vh-16px)] md:h-[calc(100vh-32px)] max-w-[1400px] bg-white rounded-lg shadow-xl flex flex-col overflow-hidden mx-2"
      >
        {/* Header */}
        <div className="flex justify-between items-center px-4 md:px-6 py-4 border-b border-gray-300 bg-white">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900">
            {step === 1 && "Select Printer Type"}
            {step === 2 && "Scan for Printers"}
          </h2>
          <button
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 md:p-6 bg-gray-50 overflow-y-auto">
          {error ? (
            <div className="h-full flex flex-col items-center justify-center">
              <div className="flex flex-col items-center text-center max-w-md">
                <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mb-4">
                  {isRetrying ? (
                    <RefreshCcw className="w-10 h-10 text-blue-500 animate-spin" />
                  ) : (
                    <AlertCircle className="w-10 h-10 text-red-500" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {isRetrying
                    ? "Retrying Connection..."
                    : "Printer Discovery Failed"}
                </h3>
                <p className="text-gray-500 mb-6">{error}</p>
                {!isRetrying && (
                  <button
                    onClick={handleRetry}
                    className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <RefreshCcw className="w-4 h-4 mr-2" />
                    Try Again
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Step 1: Printer Type Selection */}
              {step === 1 && (
                <div className="max-w-4xl mx-auto">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {PRINTER_TYPES.map((type) => (
                      <div
                        key={type.id}
                        onClick={() => handleTypeSelect(type)}
                        className={`bg-white p-4 md:p-6 rounded-xl border transition-all transform hover:scale-[1.02] ${
                          type.enabled
                            ? "hover:border-blue-500 hover:shadow-lg cursor-pointer"
                            : "opacity-50 cursor-not-allowed"
                        }`}
                      >
                        <div className="flex flex-col items-center text-center gap-4">
                          <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-blue-50 flex items-center justify-center">
                            <type.icon className="w-6 h-6 md:w-8 md:h-8 text-blue-500" />
                          </div>
                          <div>
                            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">
                              {type.name}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {type.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isLoading && (
                <div className="h-full flex flex-col md:flex-row">
                  {/* Scanning Section - Always Centered */}
                  <div className="flex-1 flex items-center justify-center min-h-[300px]">
                    <div className="relative inline-block">
                      {/* Base circle */}
                      <div className="w-32 h-32 m-auto md:w-40 md:h-40 rounded-full bg-blue-500 bg-opacity-5 flex items-center justify-center">
                        <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border border-blue-200 flex items-center justify-center animate-fade">
                          <Printer className="w-8 h-8 md:w-12 md:h-12 text-blue-400" />
                        </div>
                      </div>

                      {/* Ripple circles */}
                      {[...Array(2)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute inset-0 rounded-full border-2 border-blue-300 animate-ripple"
                          style={{
                            animationDelay: `${i * 1}s`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Scanning Interface */}
              {!isLoading && selectedType === "bluetooth" && step === 2 && (
                <div className="h-full flex flex-col md:flex-row">
                  {!isReactNativeWebView() && (
                    <div className="w-full max-w-xl mx-auto mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm text-amber-950">
                      <p className="font-semibold text-amber-900">
                        Printing from Chrome / the browser
                      </p>
                      <p className="mt-1 text-amber-900/90">
                        Bluetooth scanning in this dialog talks to the{" "}
                        <strong>QRapid mobile app</strong> only. In the browser,
                        use <strong>Print Bill</strong> on an order: Chrome will
                        list nearby BLE devices (thermal printers, etc.). Add a
                        Bluetooth printer here mainly to set name, role
                        (Bill/KOT), and paper width—or enable{" "}
                        <code className="rounded bg-amber-100/80 px-1">
                          qrapid_webBluetoothThermalBill
                        </code>{" "}
                        in localStorage / env as documented.
                      </p>
                    </div>
                  )}
                  {/* Scanning Section - Always Centered */}
                  <div className="flex-1 flex items-center justify-center min-h-[300px]">
                    <div className="text-center">
                      {bluetoothError && (
                        <div className="text-red-500 mb-4">
                          {bluetoothError}
                        </div>
                      )}

                      {bluetoothStatus === "PoweredOff" && (
                        <div className="mb-4">
                          <p className="text-amber-600">
                            Bluetooth is turned off
                          </p>
                          <button
                            onClick={enableBluetooth}
                            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                          >
                            Turn On Bluetooth
                          </button>
                        </div>
                      )}

                      {bluetoothStatus === "Unauthorized" && (
                        <div className="mb-4">
                          <p className="text-amber-600">
                            Bluetooth permission required
                          </p>
                          <button
                            onClick={openSettings}
                            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                          >
                            Open Settings
                          </button>
                        </div>
                      )}

                      {isScanning ? (
                        <div className="relative inline-block">
                          {/* Base circle */}
                          <div className="w-32 h-32 m-auto md:w-40 md:h-40 rounded-full bg-blue-500 bg-opacity-5 flex items-center justify-center">
                            <div
                              className="w-24 h-24 md:w-32 md:h-32 rounded-full border border-blue-200 flex items-center justify-center"
                              style={{
                                animation: "fade 2s ease-in-out infinite",
                              }}
                            >
                              <Printer className="w-8 h-8 md:w-12 md:h-12 text-blue-400" />
                            </div>
                          </div>

                          {/* Ripple circles */}
                          {[...Array(2)].map((_, i) => (
                            <div
                              key={i}
                              className="absolute inset-0 rounded-full border border-blue-200"
                              style={{
                                animation: `ripple 2s ease-out infinite ${
                                  i * 1
                                }s`,
                              }}
                            />
                          ))}
                        </div>
                      ) : (
                        <div>
                          <button
                            onClick={simulateScan}
                            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium shadow-md hover:shadow-lg transition-all"
                            disabled={bluetoothStatus !== "PoweredOn"}
                          >
                            Scan for Printers
                          </button>
                          <p className="text-gray-500 mt-4">
                            {bluetoothStatus === "PoweredOn"
                              ? "Click to scan for nearby printers"
                              : "Enable Bluetooth to scan for printers"}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Found Printers Section */}
                  {scannedPrinters.length > 0 && (
                    <PrinterGrid
                      printers={scannedPrinters}
                      expandedPrinterId={expandedPrinterId}
                      connectingPrinter={connectingPrinter}
                      printerDetails={printerDetails}
                      LOCATIONS={LOCATIONS}
                      onPrinterClick={handlePrinterClick}
                      onPrinterDetailChange={handlePrinterDetailChange}
                      onTestPrint={handleTestPrint}
                      onSavePrinter={handleSavePrinter}
                    />
                  )}
                </div>
              )}

              {!isLoading && selectedType === "qrapidServer" && step === 2 && (
                <div>
                  <PrinterGrid
                    printers={scannedQPrinters}
                    expandedPrinterId={expandedPrinterId}
                    connectingPrinter={connectingPrinter}
                    printerDetails={printerDetails}
                    LOCATIONS={LOCATIONS}
                    onPrinterClick={handlePrinterClick}
                    onPrinterDetailChange={handlePrinterDetailChange}
                    onTestPrint={handleTestPrint}
                    onSavePrinter={handleSavePrinter}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 md:px-6 py-4 border-t border-gray-300 bg-white">
          <div className="flex justify-between">
            {/* <button
              onClick={() => step > 1 && setStep(step - 1)}
              className={`px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium ${
                step === 1 ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={step === 1}
            >
              Back
            </button> */}
          </div>
        </div>
      </div>
    </div>
  );
}
